import { init } from '@module-federation/enhanced/runtime';
import { LoginCallback } from '@okta/okta-react';
import {
  TABLET_ONLY_PROFILE_ROLE,
  type ProfilePermissions,
} from '@strength/domain';
import { FeatureFlags } from '@strength/utils';
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Navigate,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import React from 'react';
import ReactDOM from 'react-dom';
import { z } from 'zod';
import { isSelectedTeamValid } from './app/context/viewer';
import { Navbar } from './app/navbar';
import { oktaAuth } from './app/okta/auth';
import { canAccessPermissions } from './app/permissions-access';
import { tabGuard, viewGuard } from './app/route-guards';
import { LIBRARY_TABS } from './app/pages/library/library-tabs';
import { TEAM_TABS } from './app/pages/team/team-tabs';
import { OktaSecurityContext } from './app/okta/security-context';
import { ActualsPage } from './app/pages/actuals/actuals-page';
import { TabletActualsPage } from './app/pages/actuals/tablet/TabletActualsPage';
import { AdminPage } from './app/pages/admin/admin-page';
import { AiPage } from './app/pages/admin/ai/AiPage';
import { DevelopersPage } from './app/pages/admin/developers/DevelopersPage';
import { ExerciseLibraryManagementPage } from './app/pages/admin/exercise-library-management/ExerciseLibraryManagementPage';
import { MetricsPage } from './app/pages/admin/metrics/MetricsPage';
import { OrgSettingsPage } from './app/pages/admin/org-settings/OrgSettingsPage';
import { TumSyncPage } from './app/pages/admin/tum-sync/TumSyncPage';
import { WorkoutImportPage } from './app/pages/admin/workout-import/WorkoutImportPage';
import { BlockPage } from './app/pages/block/page';
import { BodyPartsPage } from './app/pages/library/body-parts/BodyPartsPage';
import { EquipmentPage } from './app/pages/library/equipment/EquipmentPage';
import { ExerciseTypesPage } from './app/pages/library/exercise-types/ExerciseTypesPage';
import { ExercisesPage } from './app/pages/library/exercises/ExercisesPage';
import { LibraryPageWrapper } from './app/pages/library/LibraryPageWrapper';
import { MaxConversionsPage } from './app/pages/library/max-conversions/MaxConversionsPage';
import { SchemesPage } from './app/pages/library/schemes/SchemesPage';
import { TagsPage } from './app/pages/library/tags/TagsPage';
import { TemplatesPage } from './app/pages/library/workout-templates/TemplatesPage';
import { LoadingPage } from './app/pages/loading';
import { LoginPage } from './app/pages/login';
import { MessagingPage } from './app/pages/messaging/MessagingPage';
import { NotesPage } from './app/pages/notes/NotesPage';
import { CalendarPage } from './app/pages/plan/calendar/CalendarPage';
import { PlanPage } from './app/pages/plan/PlanPage';
import { TimelinePage } from './app/pages/plan/timeline/TimelinePage';
import { PermissionsPage } from './app/pages/permissions/PermissionsPage';
import { ReportingPage } from './app/pages/reporting/ReportingPage';
import { ActivityLogPage } from './app/pages/team/activity-log/activity-log-page';
import { BenchmarksPage } from './app/pages/team/benchmarks/BenchmarksPage';
import { TeamPageWrapper } from './app/pages/team/page';
import { ProfilesPage } from './app/pages/team/profiles-page';
import { TeamSettingsPage } from './app/pages/team/TeamSettingsPage';
import { AuthContext, loginWithAxleToken, useAuth } from './auth';
import { LocoSyncProvider } from './loco-sync';
import {
  actualsRedirectTarget,
  blockRedirectTarget,
  libraryRedirectTarget,
  notesRedirectTarget,
  planRedirectTarget,
  teamRedirectTarget,
} from './route-redirect-targets';
import { getTrpcProxyClient, isTRPCClientError } from './trpc';
import { readStoredSelectedTeam } from './app/context/selected-team-storage';

// Initialize module federation at runtime in the router
// to ensure the federation setup is complete before
// any routes that might need to load remote modules are accessed.
// This init function sets up our application to be a Module Federation Host.
//
// Register Strength's React/ReactDOM in the shared scope so remote MFEs
// (e.g. messaging) reuse this exact instance instead of bundling their own.
// Without this, hooks called inside a remote run against a different React
// instance than the one mounted to the DOM, producing "Invalid hook call"
// errors and `removeChild` failures.
init({
  name: 'teamworks_strength',
  remotes: [],
  shared: {
    react: {
      version: React.version,
      lib: () => React,
      shareConfig: { singleton: true, requiredVersion: '>=18' },
    },
    'react-dom': {
      version: ReactDOM.version,
      lib: () => ReactDOM,
      shareConfig: { singleton: true, requiredVersion: '>=18' },
    },
  },
});

type RouterContext = AuthContext & {
  selectedTeam: string;
  setSelectedTeam: (teamId: string) => void;
  flags: FeatureFlags;
  /** Cached permissions read for beforeLoad guards (SC-4792). */
  ensurePermissions: () => Promise<ProfilePermissions | null | undefined>;
};

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => (
    <OktaSecurityContext oktaAuth={oktaAuth}>
      <Outlet />
    </OktaSecurityContext>
  ),
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: () => {
    const { auth } = useAuth();
    if (auth?.type === 'org') {
      return getDefaultPathForOrg();
    } else {
      return <Navigate to={loginRoute.to} />;
    }
  },
});

const zAxleRedirectToken = z.object({
  token: z.string(),
});

const axleRedirectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/axle',
  validateSearch: (search) => zAxleRedirectToken.parse(search),
  beforeLoad: async ({ context, search }) => {
    try {
      await loginWithAxleToken(search.token, context.setAuth);
    } catch {
      throw redirect({ to: loginRoute.to, replace: true });
    }
  },
  component: () => <Navigate to={plansRoute.to} />,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'login',
  component: LoginPage,
});

const loginCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'login/callback',
  component: LoginCallback,
});

export const notFoundRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '*',
  component: () => (
    <div className="flex items-center justify-center h-full text-2xl">
      404 Not Found
    </div>
  ),
});

const adminAuthRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'adminAuth',
  beforeLoad: ({ context }) => {
    // If the user is logged out, redirect them to the login page
    if (!context.auth) {
      throw redirect({
        to: loginRoute.to,
      });
    } else if (context.auth.type === 'org') {
      throw redirect({
        // TODO: What to do here?
        to: plansRoute.to,
      });
    }
  },
  component: () => {
    const { auth } = useAuth();
    // This covers the case when we lose auth (aka via 401) after the beforeLoad hook
    if (!auth) {
      return <Navigate to={loginRoute.to} />;
    } else if (auth.type === 'org') {
      return getDefaultPathForOrg();
    }

    return (
      <Navbar>
        <Outlet />
      </Navbar>
    );
  },
});

const adminRoute = createRoute({
  getParentRoute: () => adminAuthRoute,
  path: 'admin/orgs',
  component: () => <AdminPage />,
});

const orgSettingsRoute = createRoute({
  getParentRoute: () => adminAuthRoute,
  path: 'admin/orgs/$orgId/settings',
  component: () => <OrgSettingsPage />,
});

const exerciseLibraryManagementRoute = createRoute({
  getParentRoute: () => adminAuthRoute,
  path: 'admin/exercise-library-management',
  component: () => <ExerciseLibraryManagementPage />,
});

const adminMetricsRoute = createRoute({
  getParentRoute: () => adminAuthRoute,
  path: 'admin/metrics',
  component: () => <MetricsPage />,
});

const workoutImportRoute = createRoute({
  getParentRoute: () => adminAuthRoute,
  path: 'admin/workout-import',
  beforeLoad: ({ context }) => {
    if (!context.flags.workoutImporterFlag) {
      throw redirect({ to: notFoundRoute.to });
    }
  },
  component: () => <WorkoutImportPage />,
});

const tumSyncRoute = createRoute({
  getParentRoute: () => adminAuthRoute,
  path: 'admin/tum-sync',
  component: () => <TumSyncPage />,
});

const developersRoute = createRoute({
  getParentRoute: () => adminAuthRoute,
  path: 'admin/developers',
  beforeLoad: ({ context }) => {
    if (!context.flags.developerToolsFlag) {
      throw redirect({ to: notFoundRoute.to });
    }
  },
  component: () => <DevelopersPage />,
});

const adminAiRoute = createRoute({
  getParentRoute: () => adminAuthRoute,
  path: 'admin/ai',
  component: () => <AiPage />,
  beforeLoad: ({ context }) => {
    if (!context.flags.aiFlag) {
      throw redirect({ to: notFoundRoute.to });
    }
  },
});

const adminIndexRoute = createRoute({
  getParentRoute: () => adminAuthRoute,
  path: 'admin',
  beforeLoad: () => {
    throw redirect({
      to: '/admin/orgs',
    });
  },
});

export const orgAuthRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'orgAuth',
  beforeLoad: async ({ context, params }) => {
    if (params && 'teamId' in params && params.teamId) {
      const teamFromURL = params?.teamId?.toString();
      try {
        const trpc = getTrpcProxyClient(() => context.setAuth(null));
        const teams = await trpc.roster.teamsPreview.query();
        const firstTeam = teams[0];
        if (isSelectedTeamValid(teamFromURL, teams)) {
          context.setSelectedTeam(teamFromURL);
        } else if (firstTeam?.id) {
          throw redirect({
            to: '/$teamId/plan',
            params: { teamId: firstTeam.id },
          });
        } else {
          throw redirect({ to: plansRoute.to, replace: true });
        }
      } catch (e) {
        if (isTRPCClientError(e)) {
          // TRPC error (likely 401 unauthorized) - redirect to login
          // Auth state cleared by onAuthError callback above
          throw redirect({ to: loginRoute.to, replace: true });
        }
        throw e;
      }
    }
    // If the user is logged out, redirect them to the login page
    if (!context.auth) {
      throw redirect({
        to: loginRoute.to,
      });
    } else if (context.auth.type === 'admin') {
      throw redirect({
        // TODO: What to do here?
        to: adminRoute.to,
      });
    } else if (context.auth.type === 'org') {
      // If selectedTeam is an invalid string (e.g., deleted team), reset to first team.
      // Empty string '' is valid (represents base library), so we don't override it.
      // pages/routes that don't support base library, but require a team scope should have their own beforeLoad hook to set the selected team to the first team if not already set.
      const trpc = getTrpcProxyClient(() => context.setAuth(null));
      const teams = await trpc.roster.teamsPreview.query();
      if (
        !isSelectedTeamValid(context.selectedTeam, teams) &&
        context.selectedTeam !== ''
      ) {
        const firstTeam = teams[0];
        context.setSelectedTeam(firstTeam?.id ?? '');
      }
    }
  },
  component: () => {
    const { auth } = useAuth();
    // This covers the case when we lose auth (aka via 401) after the beforeLoad hook
    if (!auth) {
      return <Navigate to={loginRoute.to} />;
    } else if (auth.type === 'admin') {
      return <Navigate to={adminRoute.to} />;
    }

    return <Outlet />;
  },
});

/**
 * Org With Navigation Routes
 */

function OrgWithNavLayout() {
  return (
    <Navbar>
      <LocoSyncProvider>
        <Outlet />
      </LocoSyncProvider>
    </Navbar>
  );
}

const orgWithNavRoute = createRoute({
  getParentRoute: () => orgAuthRoute,
  id: 'orgWithNav',
  beforeLoad: async ({ context }) => {
    // Check if the user is a tablet-only coach and redirect them
    if (
      context.auth?.type === 'org' &&
      context.auth.role === TABLET_ONLY_PROFILE_ROLE
    ) {
      throw redirect({ to: tabletActualsRoute.to, replace: true });
    }
  },
  component: OrgWithNavLayout,
});

const orgNoNavRoute = createRoute({
  getParentRoute: () => orgAuthRoute,
  id: 'orgNoNav',
  component: () => {
    return (
      <LocoSyncProvider>
        <Outlet />
      </LocoSyncProvider>
    );
  },
});

/**
 * Plan Routes
 */
const plansRoute = createRoute({
  getParentRoute: () => orgWithNavRoute,
  path: 'plan',
  beforeLoad: ({ context }) => {
    return beforeLoadPickTeam(context, planRedirectTarget);
  },
  // Add fallback component for edge cases
  component: () => <Navigate to={loginRoute.to} />,
});

// Parent plan route - renders PlanPage with tabs
const planRoute = createRoute({
  getParentRoute: () => orgWithNavRoute,
  path: '$teamId/plan',
  component: PlanPage,
});

// Timeline child route
const planTimelineRoute = createRoute({
  getParentRoute: () => planRoute,
  path: 'timeline',
  component: () => <TimelinePage />,
});

// Calendar child route
const planCalendarRoute = createRoute({
  getParentRoute: () => planRoute,
  path: 'calendar',
  component: () => <CalendarPage />,
});

// Index route for /$teamId/plan - redirect to timeline
const planIndexRoute = createRoute({
  getParentRoute: () => planRoute,
  path: '/',
  beforeLoad: ({ params }) => {
    throw redirect({
      to: '/$teamId/plan/timeline',
      params: { teamId: params.teamId },
    });
  },
});

/**
 * Library Routers
 */

// Legacy redirect: /library → /$teamId/library/exercises
const libraryLegacyRoute = createRoute({
  getParentRoute: () => orgWithNavRoute,
  path: 'library',
  beforeLoad: ({ context }) =>
    beforeLoadPickTeam(context, libraryRedirectTarget),
});

const libraryRoute = createRoute({
  getParentRoute: () => orgWithNavRoute,
  path: '$teamId/library',
  component: LibraryPageWrapper,
});

const exercisesRoute = createRoute({
  getParentRoute: () => libraryRoute,
  path: 'exercises',
  beforeLoad: tabGuard(LIBRARY_TABS, '/$teamId/library/exercises'),
  component: () => <ExercisesPage />,
});

const schemesRoute = createRoute({
  getParentRoute: () => libraryRoute,
  path: 'schemes',
  beforeLoad: tabGuard(LIBRARY_TABS, '/$teamId/library/schemes'),
  component: () => <SchemesPage />,
});

const exerciseMaxConversionsRoute = createRoute({
  getParentRoute: () => libraryRoute,
  path: 'max-conversions',
  beforeLoad: tabGuard(LIBRARY_TABS, '/$teamId/library/max-conversions'),
  component: () => <MaxConversionsPage />,
});

const exerciseWorkoutTemplatesRoute = createRoute({
  getParentRoute: () => libraryRoute,
  path: 'workout-templates',
  beforeLoad: tabGuard(LIBRARY_TABS, '/$teamId/library/workout-templates'),
  component: () => <TemplatesPage />,
});

const tagsRoute = createRoute({
  getParentRoute: () => libraryRoute,
  path: 'tags',
  beforeLoad: tabGuard(LIBRARY_TABS, '/$teamId/library/tags'),
  component: () => <TagsPage />,
});

const exerciseTypesRoute = createRoute({
  getParentRoute: () => libraryRoute,
  path: 'exercise-types',
  beforeLoad: tabGuard(LIBRARY_TABS, '/$teamId/library/exercise-types'),
  component: () => <ExerciseTypesPage />,
});

const equipmentRoute = createRoute({
  getParentRoute: () => libraryRoute,
  path: 'equipment',
  beforeLoad: tabGuard(LIBRARY_TABS, '/$teamId/library/equipment'),
  component: () => <EquipmentPage />,
});

const bodyPartsRoute = createRoute({
  getParentRoute: () => libraryRoute,
  path: 'body-parts',
  beforeLoad: tabGuard(LIBRARY_TABS, '/$teamId/library/body-parts'),
  component: () => <BodyPartsPage />,
});

// Index route for /$teamId/library - redirect to exercises
const libraryIndexRoute = createRoute({
  getParentRoute: () => libraryRoute,
  path: '/',
  beforeLoad: ({ params }) => {
    throw redirect({
      to: '/$teamId/library/exercises',
      params: { teamId: params.teamId },
    });
  },
});

/**
 * Team Routers
 */

/**
 * beforeLoad helper for legacy team-scoped paths (`/team`, `/library`,
 * `/actuals`, `/notes`, `/block/$blockId`) that have no teamId param:
 * resolves the team to route with — stored selection first, then the
 * router context, then teams[0] — and redirects to the area's target.
 * A failed teams fetch redirects to login (auth state cleared).
 */
async function beforeLoadPickTeam(
  context: RouterContext,
  getRedirect: (teamId: string) => Parameters<typeof redirect>[0],
) {
  try {
    // Is this a good idea to do every time?
    const trpc = getTrpcProxyClient(() => context.setAuth(null));
    const teams = await trpc.roster.teamsPreview.query();
    const firstTeam = teams[0];

    // localStorage comes first, not context.selectedTeam: on the first
    // navigation after login the router has already snapshotted its context
    // (loadMatches in @tanstack/react-router inherits the parent match's
    // context per load), while the state app.tsx backfills from storage only
    // lands one commit after auth resolves. Reading storage here means a
    // returning user gets their last team instead of teams[0] — and stops
    // that teams[0] redirect from overwriting the stored team via the
    // setSelectedTeam call in orgAuthRoute's teamId branch.
    const storedTeam =
      context.auth?.type === 'org'
        ? readStoredSelectedTeam(context.auth.userId, context.auth.orgId)
        : '';

    // on initial load of a page, if the stored or selected team is valid, use it
    if (isSelectedTeamValid(storedTeam, teams)) {
      throw redirect(getRedirect(storedTeam));
    } else if (isSelectedTeamValid(context.selectedTeam, teams)) {
      throw redirect(getRedirect(context.selectedTeam));
    } else if (firstTeam) {
      throw redirect(getRedirect(firstTeam.id));
    } else {
      // Not sure what to do here
    }
  } catch (e) {
    if (isTRPCClientError(e)) {
      // TRPC error (likely 401 unauthorized) - redirect to login
      // Auth state cleared by onAuthError callback above
      throw redirect({ to: loginRoute.to, replace: true });
    } else {
      // Probably the redirect above, throw
      throw e;
    }
  }
}

const teamsRoute = createRoute({
  getParentRoute: () => orgWithNavRoute,
  path: 'team',
  beforeLoad: ({ context }) => beforeLoadPickTeam(context, teamRedirectTarget),
});

const teamRoute = createRoute({
  getParentRoute: () => orgWithNavRoute,
  path: '$teamId/team',
  component: TeamPageWrapper,
});

const teamProfilesRoute = createRoute({
  getParentRoute: () => teamRoute,
  path: 'profiles',
  beforeLoad: tabGuard(TEAM_TABS, '/$teamId/team/profiles'),
  component: () => <ProfilesPage />,
});

const teamBenchmarksRoute = createRoute({
  getParentRoute: () => teamRoute,
  path: 'benchmarks',
  beforeLoad: tabGuard(TEAM_TABS, '/$teamId/team/benchmarks'),
  component: () => <BenchmarksPage />,
});

const teamSettingsRoute = createRoute({
  getParentRoute: () => teamRoute,
  path: 'settings',
  beforeLoad: tabGuard(TEAM_TABS, '/$teamId/team/settings'),
  component: () => <TeamSettingsPage />,
});

const teamActivityLogRoute = createRoute({
  getParentRoute: () => teamRoute,
  path: 'activity-log',
  beforeLoad: tabGuard(TEAM_TABS, '/$teamId/team/activity-log'),
  component: () => <ActivityLogPage />,
});

const teamNotFoundRoute = createRoute({
  getParentRoute: () => teamRoute,
  path: '*',
  component: ({ params }) => (
    <Navigate to="/$teamId/team/profiles" params={params} />
  ),
});

/**
 * Actuals Routers
 */
const actualsRoute = createRoute({
  getParentRoute: () => orgWithNavRoute,
  path: 'actuals',
  beforeLoad: ({ context }) =>
    beforeLoadPickTeam(context, actualsRedirectTarget),
});

const teamActualsIndexRoute = createRoute({
  getParentRoute: () => orgWithNavRoute,
  path: '$teamId/actuals',
  beforeLoad: ({ params }) => {
    throw redirect(actualsRedirectTarget(params.teamId));
  },
});

const actualRoute = createRoute({
  getParentRoute: () => orgWithNavRoute,
  path: '$teamId/actuals/$date',
  // Actuals View revoked → redirect; the $teamId/actuals index redirect
  // targets this route, so it is guarded transitively.
  beforeLoad: viewGuard('actuals'),
  component: () => <ActualsPage />,
});

const tabletActualsRoute = createRoute({
  getParentRoute: () => orgNoNavRoute,
  path: 'actuals/tablet',
  // Fails closed: the TeamCoach_TabletAthlete sync-group governance does not
  // cover the tablet.weightUnits / tablet.addExerciseNote procedures, so a
  // fetch error must never leave the tablet workflow mounted.
  beforeLoad: viewGuard('tablet'),
  component: () => <TabletActualsPage />,
});

/**
 * Training Block Routers
 */

// Legacy redirect: /block/$blockId → /$teamId/block/$blockId
const blocksLegacyRoute = createRoute({
  getParentRoute: () => orgWithNavRoute,
  path: 'block/$blockId',
  beforeLoad: ({ context, params }) =>
    beforeLoadPickTeam(context, (teamId) =>
      blockRedirectTarget(teamId, params.blockId),
    ),
});

const blocksRoute = createRoute({
  getParentRoute: () => orgWithNavRoute,
  path: '$teamId/block/$blockId',
  validateSearch: z.object({
    athleteId: z.string().optional(),
    workoutId: z.string().optional(),
    showActuals: z.boolean().optional(),
    crossTeamId: z.string().optional(),
  }),
  component: () => <BlockPage />,
});

/*
 * Notes Routers
 */

// Legacy redirect: /notes → /$teamId/notes
const notesLegacyRoute = createRoute({
  getParentRoute: () => orgWithNavRoute,
  path: 'notes',
  beforeLoad: ({ context }) => beforeLoadPickTeam(context, notesRedirectTarget),
});

const notesRoute = createRoute({
  getParentRoute: () => orgWithNavRoute,
  path: '$teamId/notes',
  // Notes View revoked → redirect; the legacy /notes redirect targets this
  // route, so it is guarded transitively.
  beforeLoad: viewGuard('notes'),
  component: () => <NotesPage />,
});

/*
 * Reporting Routers
 */
const reportingRoute = createRoute({
  getParentRoute: () => orgWithNavRoute,
  path: 'reporting',
  beforeLoad: async ({ context }) => {
    const auth = context.auth;
    if (auth?.type !== 'org') {
      throw redirect({ to: notFoundRoute.to });
    }
    const trpcClient = getTrpcProxyClient(() => context.setAuth(null));
    let hasAmsDashboardAccess = false;
    try {
      const personDoc = await trpcClient.admin.axlePersonDocument.query({
        profileId: auth.profileId,
        orgId: auth.orgId,
      });
      hasAmsDashboardAccess = !!(
        personDoc?._document.login_access?.web?.ams ||
        personDoc?._document.login_access?.web?.hp
      );
    } catch {
      // treat inability to fetch person document as no access
    }
    if (!hasAmsDashboardAccess) {
      throw redirect({ to: notFoundRoute.to });
    }
  },
  component: () => <ReportingPage />,
});

/*
 * Messaging Routers
 */
const messagingRoute = createRoute({
  getParentRoute: () => orgWithNavRoute,
  path: 'messaging',
  component: () => <MessagingPage />,
});
// need this wildcard route to catch all routes within the MFE
const messagingWildcardRoute = createRoute({
  getParentRoute: () => orgWithNavRoute,
  path: 'messaging/*',
  component: () => <MessagingPage />,
});

const permissionsRoute = createRoute({
  getParentRoute: () => orgWithNavRoute,
  path: 'permissions',
  beforeLoad: ({ context }) => {
    if (
      context.auth?.type !== 'org' ||
      !canAccessPermissions(context.auth.role, true)
    ) {
      throw redirect({ to: notFoundRoute.to });
    }
  },
  component: () => <PermissionsPage />,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  loginCallbackRoute,
  notFoundRoute,
  axleRedirectRoute,
  orgAuthRoute,
  orgWithNavRoute.addChildren([
    plansRoute,
    planRoute.addChildren([
      planIndexRoute,
      planTimelineRoute,
      planCalendarRoute,
    ]),
    blocksLegacyRoute,
    blocksRoute,
    teamsRoute,
    reportingRoute,
    messagingRoute,
    messagingWildcardRoute,
    permissionsRoute,
    libraryLegacyRoute,
    libraryRoute.addChildren([
      libraryIndexRoute,
      exercisesRoute,
      schemesRoute,
      exerciseMaxConversionsRoute,
      exerciseWorkoutTemplatesRoute,
      tagsRoute,
      exerciseTypesRoute,
      equipmentRoute,
      bodyPartsRoute,
    ]),
    teamRoute.addChildren([
      teamProfilesRoute,
      teamBenchmarksRoute,
      teamSettingsRoute,
      teamActivityLogRoute,
      teamNotFoundRoute,
    ]),
    teamActualsIndexRoute,
    actualRoute,
    actualsRoute,
    notesLegacyRoute,
    notesRoute,
  ]),
  orgNoNavRoute.addChildren([tabletActualsRoute]),
  adminAuthRoute.addChildren([
    adminIndexRoute,
    adminRoute,
    orgSettingsRoute,
    exerciseLibraryManagementRoute,
    adminMetricsRoute,
    workoutImportRoute,
    tumSyncRoute,
    developersRoute,
    adminAiRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  context: undefined!, // We'll inject this when we render
  // Adds a React.Suspense boundary to every route match so that TanStack
  // Router's internal `throw match.loadPromise` (emitted while a route's
  // beforeLoad is still in-flight) is caught by Suspense rather than
  // propagating to React as a synchronous-input suspension (error #426).
  // Navigation transitions are still wrapped in startTransition, so the
  // previous page stays visible until loading completes; LoadingPage only
  // appears for slow navigations exceeding defaultPendingMs (1 s default).
  defaultPendingComponent: LoadingPage,
});

/**
 * Org landing decision: where an authenticated org user lands at `/`.
 * replace mirrors the viewGuard redirect so Back from plan cannot re-land
 * on `/` and re-trigger the navigation.
 */
const getDefaultPathForOrg = () => {
  // SC-219: temp workout to always show desktop mode
  // if (false) {
  //   return <Navigate to={tabletActualsRoute.to} />;
  // }
  // replace: mirrors the viewGuard redirect so Back from plan cannot
  // re-land on `/` and re-trigger the navigation.
  return <Navigate to={plansRoute.to} replace />;
};

// Register your router for maximum type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
